/**
 * Domain → design-token alias mapping for Scene Tree domain badges.
 *
 * Each entry maps a `TreeNodeDomain` to:
 *   - `tokenAlias`  — a dot-path token alias (e.g. `"color.scene-tree.domain.building"`).
 *                     These aliases will be resolved in the platform design tokens file;
 *                     the component converts them to CSS custom-property references at
 *                     render time via `tokenAliasToCssVar(alias)`.
 *   - `label`       — short human-readable label for the badge chip.
 *   - `ariaLabel`   — accessible label for screen readers.
 *
 * Design constraints (per OBJECT_IDENTITY_AND_SELECTION_POLICY §7, DEC-50-02):
 *   - Zero domain branches in rendering code. This map IS the only "domain knowledge".
 *   - Raw hex/rgb values are forbidden here; only token alias strings are stored.
 *   - Per-domain colours must derive from the shared design token namespace.
 *
 * Source: 50-ui-design-brief.md §7.2.
 */

import type { TreeNodeDomain } from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";

export interface DomainTokenEntry {
  /**
   * Dot-path token alias for the badge background/foreground.
   * Converts to CSS variable: `"color.scene-tree.domain.building"` →
   * `var(--color-scene-tree-domain-building, <fallback>)`.
   */
  readonly tokenAlias: string;
  /** Short badge label shown in the chip. */
  readonly label: string;
  /** Full accessible label for screen readers. */
  readonly ariaLabel: string;
}

export const DOMAIN_TOKEN_MAP: Record<TreeNodeDomain, DomainTokenEntry> = {
  building: {
    tokenAlias:  "color.scene-tree.domain.building",
    label:       "Building / BIM",
    ariaLabel:   "Domain: Building / BIM",
  },
  plant: {
    tokenAlias:  "color.scene-tree.domain.plant",
    label:       "Plant / CFIHOS",
    ariaLabel:   "Domain: Plant / CFIHOS",
  },
  utility: {
    tokenAlias:  "color.scene-tree.domain.utility",
    label:       "Utility",
    ariaLabel:   "Domain: Utility",
  },
  infrastructure: {
    tokenAlias:  "color.scene-tree.domain.infrastructure",
    label:       "Infrastructure",
    ariaLabel:   "Domain: Infrastructure",
  },
  subsea: {
    tokenAlias:  "color.scene-tree.domain.subsea",
    label:       "Subsea",
    ariaLabel:   "Domain: Subsea",
  },
  survey: {
    tokenAlias:  "color.scene-tree.domain.survey",
    label:       "Survey / Point Cloud",
    ariaLabel:   "Domain: Survey / Point Cloud",
  },
  smartcity: {
    tokenAlias:  "color.scene-tree.domain.smartcity",
    label:       "Smart City",
    ariaLabel:   "Domain: Smart City",
  },
  defence: {
    tokenAlias:  "color.scene-tree.domain.defence",
    label:       "Defence",
    ariaLabel:   "Domain: Defence",
  },
  generic: {
    tokenAlias:  "color.scene-tree.domain.generic",
    label:       "Generic",
    ariaLabel:   "Domain: Generic",
  },
};

/**
 * Converts a dot-path token alias to a CSS custom-property reference.
 *
 * @example
 *   tokenAliasToCssVar("color.scene-tree.domain.building", "#243330")
 *   // → "var(--color-scene-tree-domain-building, #243330)"
 */
export function tokenAliasToCssVar(alias: string, fallback: string): string {
  const varName = `--${alias.replace(/\./g, "-")}`;
  return `var(${varName}, ${fallback})`;
}
